import {
  CURRENT_USER_REF_ID_STORAGE_KEY,
  getSafeLocalStorage,
  getUserStorageScope,
  rehydrateUserScopedAtoms,
} from '@heidi/utils/jotai-storage';
import { useUser } from '@heidi/workspace/data';
import { useStore } from 'jotai';
import { useEffect, useRef } from 'react';

/**
 * Caches `user_ref_id` so per-user session-list storage keys resolve at the next
 * load's `getOnInit` read; cleared on logout (see `logoutStorage.ts`). When it
 * was NOT cached at boot (first login / re-login), that read missed, so once the
 * id resolves we rehydrate the user-scoped atoms. The cold-refresh path (id
 * already cached) skips rehydrate to avoid clobbering an in-flight change.
 */
export const useCacheUserRefIdForStorage = (): void => {
  const store = useStore();
  const { data: userRefId } = useUser((user) => user.user_ref_id);
  // The id the user-scoped atoms currently reflect — the boot read for the first
  // render, then advanced after each rehydrate so an account switch back to a
  // prior id still re-reads. `undefined` = not yet captured (`getUserStorageScope`
  // returns `string | null`).
  const reflectedIdRef = useRef<string | null | undefined>(undefined);
  if (reflectedIdRef.current === undefined) {
    reflectedIdRef.current = getUserStorageScope();
  }
  const appliedRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!userRefId || userRefId === appliedRef.current) return;
    getSafeLocalStorage().setItem(CURRENT_USER_REF_ID_STORAGE_KEY, userRefId);
    appliedRef.current = userRefId;
    if (reflectedIdRef.current !== userRefId) {
      rehydrateUserScopedAtoms(store);
      reflectedIdRef.current = userRefId;
    }
  }, [userRefId, store]);
};
